import { useHttpClient } from "@/composables/UseHttpClient";
import { useMutation, useQuery } from "@tanstack/vue-query";
import type {
  WorkspaceV1,
  TerritoriesAIApiV1CommonResponse,
  TerritoriesAiApiV1SwitchWorkspacePostRequest,
  TerritoriesAiApiV1SwitchWorkspacePostResponse,
  BillingPlanV1
} from "@territoriesai/types";
import {
  createRandomBillingPlan,
  createRandomWorkspace,
  createRandomWorkspaceUserGroup
} from "./mock/mock-data-generator";
import _ from "lodash";
import { useSessionStore } from "@/stores/session.store";
import type { AxiosError } from "axios";
import { toValue, type Ref } from "vue";

export interface MockTerritoriesAIApiV1WorkspacePostPutRequest {
  workspace: WorkspaceV1;
}

export interface MockTerritoriesAIApiV1WorkspacePostPutResponse
  extends TerritoriesAIApiV1CommonResponse {
  workspace: WorkspaceV1;
}

export const useUpdateWorkspace = () => {
  const { request } = useHttpClient({
    method: "put"
  });

  return useMutation({
    mutationKey: ["update-workspace"],
    mutationFn: async (
      updatedWorkspace: MockTerritoriesAIApiV1WorkspacePostPutRequest
    ) => {
      try {
        const { workspace, status, errorMessage } =
          await request<MockTerritoriesAIApiV1WorkspacePostPutResponse>(
            { url: "/s/v1/workspace", data: updatedWorkspace },
            {
              mockData: () => ({
                status: "OK",
                workspace: createRandomWorkspace()
              })
            }
          );

        return workspace;
      } catch (error) {
        console.error((error as Error).message);
        throw error;
      }
    }
  });
};

export const useSwitchWorkspace = () => {
  const { request } = useHttpClient<
    TerritoriesAiApiV1SwitchWorkspacePostRequest,
    never
  >({
    method: "post"
  });

  return useMutation({
    mutationKey: ["switch-workspace"],
    mutationFn: async (data: TerritoriesAiApiV1SwitchWorkspacePostRequest) => {
      try {
        const { status, errorMessage, session } =
          await request<TerritoriesAiApiV1SwitchWorkspacePostResponse>(
            { url: `/s/v1/workspace/switch`, data },
            {
              mockData: () => ({
                status: "OK",
                session: {
                  version: 2,
                  idToken: useSessionStore().selectedSession()?.idToken || "",
                  refreshToken:
                    useSessionStore().selectedSession()?.refreshToken || "",
                  workspaceUserGroup: createRandomWorkspaceUserGroup()
                }
              })
            }
          );

        return { session };
      } catch (error) {
        console.error((error as Error).message);
        throw new Error(
          (error as AxiosError<TerritoriesAiApiV1SwitchWorkspacePostResponse>)
            ?.response?.data?.errorMessage || (error as Error).message
        );
      }
    }
  });
};

export const useGetWorkspaceById = (workspaceId: Ref<string> | string) => {
  const { request } = useHttpClient({
    method: "get"
  });

  return useQuery<
    {
      workspace: WorkspaceV1;
      billingPlan: BillingPlanV1;
      status: "OK" | "ERROR";
    },
    Error
  >({
    queryKey: ["get-workspace", workspaceId],
    queryFn: async () => {
      try {
        const { workspace } = await request<{
          workspace: WorkspaceV1;
          status: "OK" | "ERROR";
        }>(
          { url: `/s/v1/workspace/${toValue(workspaceId)}` },
          {
            mockData: () => ({
              status: "OK",
              workspace: createRandomWorkspace()
            })
          }
        );
        const { billingPlan } = await request<{
          billingPlan: BillingPlanV1;
          status: "OK" | "ERROR";
        }>(
          {
            url: `/s/v1/billing-plan/${workspace.currentBillingPlanId}`
          },
          {
            mockData: () => ({
              status: "OK",
              billingPlan: createRandomBillingPlan({
                id: "d731a367-b348-4054-9aac-57cfed7acd66",
                groupId: "free",
                planGroupName: "Score",
                price: 0,
                planNames: ["1,000 Credits (Free)"]
              })
            })
          }
        );

        return {
          status: "OK",
          workspace,
          billingPlan: billingPlan
        };
      } catch (error) {
        console.error((error as Error).message);
        throw error;
      }
    }
  });
};

export const useDeleteWorkspace = () => {
  const { request } = useHttpClient({
    method: "delete"
  });

  return useMutation({
    mutationKey: ["delete-workspace"],
    mutationFn: async (workspaceId: string) => {
      try {
        return await request<TerritoriesAIApiV1CommonResponse>(
          { url: `/s/v1/workspace/${workspaceId}` },
          {
            mockData: () => ({
              status: "OK"
            })
          }
        );
      } catch (error) {
        console.error((error as Error).message);
        throw error;
      }
    }
  });
};
